import React, { useRef, useState } from 'react';
import { Upload, Pencil, Loader } from 'lucide-react';
import { useEditMode } from '../context/EditModeContext';

/**
 * EditableImage
 * --------------------------------------------------------------------
 * Drop-in replacement for an <img>. In view-mode it renders an identical
 * <img>; in edit-mode it adds a hover overlay with "Upload" and "Edit alt"
 * actions.
 *
 * Props:
 *   src       string
 *   alt       string
 *   onChange  (newSrc, newAlt) => void
 *   className string  - applied to wrapper in edit-mode AND <img> in view-mode
 *   imgProps  object  - additional props spread on the <img>
 */
function EditableImage({ src, alt = '', onChange, className = '', imgProps = {} }) {
  const { editMode, upload } = useEditMode();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [editingAlt, setEditingAlt] = useState(false);
  const [altDraft, setAltDraft] = useState(alt);
  const [hovered, setHovered] = useState(false);

  if (!editMode) {
    return <img src={src} alt={alt} className={className} {...imgProps} />;
  }

  const handlePickFile = () => fileRef.current && fileRef.current.click();

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await upload(file);
      if (res && res.url) {
        onChange && onChange(res.url, alt);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const commitAlt = () => {
    setEditingAlt(false);
    if (altDraft !== alt) {
      onChange && onChange(src, altDraft);
    }
  };

  return (
    <div
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <img src={src} alt={alt} className={className} {...imgProps} />

      {(hovered || uploading || editingAlt) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/40 backdrop-blur-[1px] border border-dashed border-indigo-400 rounded-[inherit]">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handlePickFile}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-gray-800 text-xs font-medium shadow hover:bg-indigo-50 disabled:opacity-60"
            >
              {uploading ? <Loader size={14} className="animate-spin" /> : <Upload size={14} />}
              {uploading ? 'Uploading…' : 'Upload'}
            </button>
            <button
              type="button"
              onClick={() => {
                setAltDraft(alt);
                setEditingAlt((v) => !v);
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white text-gray-800 text-xs font-medium shadow hover:bg-indigo-50"
            >
              <Pencil size={14} />
              Edit alt
            </button>
          </div>

          {editingAlt && (
            <input
              autoFocus
              type="text"
              value={altDraft}
              onChange={(e) => setAltDraft(e.target.value)}
              onBlur={commitAlt}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitAlt();
                if (e.key === 'Escape') setEditingAlt(false);
              }}
              placeholder="Alt text"
              className="w-3/4 px-2 py-1 text-xs rounded border border-gray-300 bg-white text-gray-800"
            />
          )}
        </div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
}

export default EditableImage;
